import React, { useState, useEffect } from 'react';
import { FileText, AlertTriangle, Shield, Clock, ChevronDown, ExternalLink, Sparkles } from 'lucide-react';
import api from '../../services/api';

const TYPE_COLORS = {
  court_order: 'bg-purple-900/40 text-purple-300 border-purple-700',
  motion: 'bg-blue-900/40 text-blue-300 border-blue-700',
  pleading: 'bg-blue-900/40 text-blue-300 border-blue-700',
  settlement_offer: 'bg-amber-900/40 text-amber-300 border-amber-700',
  demand_letter: 'bg-red-900/40 text-red-300 border-red-700',
  discovery: 'bg-teal-900/40 text-teal-300 border-teal-700',
  correspondence: 'bg-gray-800/50 text-gray-300 border-gray-700',
  contract: 'bg-green-900/40 text-green-300 border-green-700',
};

const DocumentIntelPanel = ({ caseId }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/cases/${caseId}/document-analyses`);
        setAnalyses(resp.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [caseId]);

  if (loading) return <div className="p-4 text-gray-500 animate-pulse">Loading document intelligence...</div>;

  if (analyses.length === 0) {
    return (
      <div className="p-8 text-center">
        <FileText className="w-8 h-8 text-gray-600 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No document analyses yet.</p>
        <p className="text-gray-600 text-xs mt-1">Documents are analyzed automatically when filed to this case.</p>
      </div>
    );
  }

  const responseRequired = analyses.filter(a => a.requires_response);
  const adverse = analyses.filter(a => a.adverse_to_client);
  const settlements = analyses.filter(a => a.document_type === 'settlement_offer');

  return (
    <div className="p-4 space-y-3">
      {/* Banners */}
      {settlements.length > 0 && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-amber-300 font-medium">{settlements.length} settlement offer{settlements.length > 1 ? 's' : ''} — review required</span>
        </div>
      )}
      {responseRequired.length > 0 && (
        <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-300">{responseRequired.length} document{responseRequired.length > 1 ? 's' : ''} require{responseRequired.length === 1 ? 's' : ''} response</span>
        </div>
      )}
      {adverse.length > 0 && (
        <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-orange-400" />
          <span className="text-sm text-orange-300">{adverse.length} adverse document{adverse.length > 1 ? 's' : ''} flagged</span>
        </div>
      )}

      {/* Document list */}
      {analyses.map(da => (
        <AnalysisCard key={da.id} da={da} caseId={caseId} />
      ))}
    </div>
  );
};

const AnalysisCard = ({ da, caseId }) => {
  const [expanded, setExpanded] = useState(false);
  const a = da.analysis || {};
  const typeColor = TYPE_COLORS[da.document_type] || TYPE_COLORS.correspondence;

  const handleGenerateDraft = async () => {
    try {
      // Use eservice filing draft endpoint or response drafter
      await api.post(`/cases/${caseId}/client-updates/generate`, { trigger: 'document_response', tone: 'professional' });
    } catch (err) { console.error(err); }
  };

  return (
    <div className={`rounded-lg border p-3 ${typeColor} transition`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800/60 text-gray-300 capitalize">{(da.document_type || 'document').replace('_', ' ')}</span>
          <span className="text-sm text-gray-200 truncate">{a.subject_matter || 'Document'}</span>
          {da.privilege_flag && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/50 text-yellow-300">PRIVILEGED</span>}
          {da.adverse_to_client && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/50 text-red-300">ADVERSE</span>}
          {da.requires_response && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-800 text-white">RESPONSE REQ</span>}
        </div>
        <div className="flex items-center gap-2 ml-2">
          {da.response_deadline && (
            <span className="text-[10px] text-red-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {new Date(da.response_deadline).toLocaleDateString()}
            </span>
          )}
          <span className="text-[10px] text-gray-600">{da.analyzed_at ? new Date(da.analyzed_at).toLocaleDateString() : ''}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-gray-700/50 space-y-2">
          {/* Summary */}
          {a.summary && <p className="text-xs text-gray-300">{a.summary}</p>}

          {/* Key facts */}
          {a.key_facts?.length > 0 && (
            <div>
              <span className="text-[10px] text-gray-500 font-medium">Key Facts:</span>
              <ul className="mt-1 space-y-0.5">
                {a.key_facts.map((f, i) => (
                  <li key={i} className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-teal-500 mt-0.5">•</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Parties */}
          {a.parties_mentioned?.length > 0 && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">Parties: </span>
              {a.parties_mentioned.join(', ')}
            </div>
          )}

          {/* Deadlines */}
          {a.deadlines?.length > 0 && (
            <div>
              <span className="text-[10px] text-gray-500 font-medium">Deadlines:</span>
              {a.deadlines.map((dl, i) => (
                <div key={i} className="text-xs text-gray-400 ml-2 flex items-center gap-2">
                  <span className="font-mono text-gray-500">{dl.date}</span>
                  <span>{dl.description}</span>
                  {dl.urgency === 'urgent' && <span className="text-red-400 text-[10px]">URGENT</span>}
                </div>
              ))}
            </div>
          )}

          {/* Action items */}
          {a.action_items?.length > 0 && (
            <div>
              <span className="text-[10px] text-gray-500 font-medium">Action Items:</span>
              {a.action_items.map((ai, i) => (
                <div key={i} className="text-xs text-gray-400 ml-2 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${ai.priority === 'urgent' ? 'bg-red-400' : ai.priority === 'normal' ? 'bg-amber-400' : 'bg-gray-500'}`} />
                  <span>{ai.action}</span>
                  {ai.deadline && <span className="text-gray-600 font-mono">{ai.deadline}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Sender/recipient */}
          <div className="flex gap-4 text-[10px] text-gray-600">
            {a.sender && <span>From: {a.sender}</span>}
            {a.recipient && <span>To: {a.recipient}</span>}
            {a.date && <span>Dated: {a.date}</span>}
          </div>

          {/* Generate response button */}
          {da.requires_response && (
            <button onClick={handleGenerateDraft}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-teal-700 hover:bg-teal-600 text-white mt-2">
              <Sparkles className="w-3.5 h-3.5" /> Generate Response Draft
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentIntelPanel;
